<template>
  <div>
    <v-row no-gutters class="mt-6 mb-3">
      <v-col>
        <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.text" tag="p" scope="global">
          <template #phoneNumber>
            <a :href="`tel:${SALES_PHONE_NUMBER_WITH_COUNTRY_CODE}`" class="text-darkblue-500 ">{{ SALES_PHONE_NUMBER }}</a>
          </template>
        </i18n-t>

        <fieldset class="mt-6">
          <h3 class="form-label required-asterisk">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogContactForm.phoneNumber') }}
          </h3>
          <AppPhoneField :value="telephone"
                         :error-messages="phoneNumberErrors"
                         @on-input="handlePhoneChange"
          />
        </fieldset>

        <fieldset class="mt-4">
          <h3 class="form-label required-asterisk">
            {{ $t('projects.dialogs.ProjectsNewDataroomDialogContactForm.requestDetails') }}
          </h3>
          <v-textarea
            v-model="v$.requestDetails.$model"
            :hide-details="requestDetailsErrors.length === 0"
            color="blue-darken-2"
            variant="outlined"
            :placeholder="$t('projects.dialogs.ProjectTypeQuoteRequestDialog.requestMessagePlaceholder')"
            :error-messages="requestDetailsErrors"
            rows="4"
          />
        </fieldset>

        <v-checkbox v-model="v$.isPrivacyPolicyCheck.$model"
                    :error-messages="termsCheckedError"
                    :hide-details="termsCheckedError.length === 0"
                    density="compact"
                    class="privacy-policy-check mt-6"
        >
          <template #label>
            <i18n-t keypath="projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyConsentText" tag="p" class="mb-0" scope="global">
              <template #privacyPolicyUrl>
                <a :href="$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicyUrl')"
                   target="_blank"
                   class="text-darkblue-500 d-inline-block"
                   @click.stop
                >
                  {{$t('projects.dialogs.ProjectTypeQuoteRequestDialog.privacyPolicy')}}
                </a>
              </template>
            </i18n-t>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>

    <!-- Exists purely to avoid reloading after submitting the form... -->
    <iframe class="d-none" name="hiddenFrame" ref="formIframe" width="0" height="0" title="hidden-contact-form-iframe"></iframe>
    <form
      ref="quoteRequestForm"
      class="d-none"
      :action="QUOTE_REQUEST_TOPICS_ENDPOINTS['DR']"
      method="post"
      target="hiddenFrame"
    >
      <input type="hidden" name="comments" :value="v$.requestDetails.$model" />
      <input type="hidden" name="phone" :value="v$.telephone.$model" />
      <input type="hidden" name="email" :value="profile.email" />
      <input type="hidden" name="firstname" :value="profile.firstName" />
      <input type="hidden" name="lastname" :value="profile.lastName" />
      <input type="hidden" name="orgname" :value="profile.company" />
      <input
        v-if="profile.subscription?.country"
        type="hidden" name="country"
        :value="profile.subscription.country"
      />
    </form>
  </div>

</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { parsePhoneNumber } from 'awesome-phonenumber'
import { mapState } from 'vuex'

import AppPhoneField from '@/common/AppPhoneField.vue'
import { SALES_PHONE_NUMBER, SALES_PHONE_NUMBER_WITH_COUNTRY_CODE } from '@/common/utils/closdInfo.js'
import { formFieldErrors } from '@/common/utils/form.ts'
import { QUOTE_REQUEST_TOPICS_ENDPOINTS } from '@/common/utils/quoteRequestTopics.js'
import { toast } from '@/common/utils/toast.ts'

// The form is submitted to the iframe since it's a standard submission,
// so we need to poll it to check if the form was successfully submitted
const FORM_SUCCESS_POLLING_INTERVAL = 50

export default {
  name: 'ProjectsNewDataroomDialogContactForm',
  components: { AppPhoneField },
  setup: () => ({ v$: useVuelidate() }),
  validations () {
    return {
      isPrivacyPolicyCheck: {
        required,
        sameAs: sameAs(true),
      },
      requestDetails: { required },
      telephone: { required, isValidPhoneNumber: this.isValidPhoneNumber },
    }
  },
  data () {
    return {
      SALES_PHONE_NUMBER,
      SALES_PHONE_NUMBER_WITH_COUNTRY_CODE,
      QUOTE_REQUEST_TOPICS_ENDPOINTS,
      telephone: '',
      requestDetails: null,
      isPrivacyPolicyCheck: false,
      quoteRequestFormIntervalTimerId: -1,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    phoneNumberErrors () {
      return formFieldErrors(
        this.v$.telephone,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.phoneNumber') })],
          ['isValidPhoneNumber', this.$t('projects.dialogs.ProjectsNewDataroomDialogContactForm.phoneNumberInvalid')],
        ],
      )
    },
    requestDetailsErrors () {
      return formFieldErrors(
        this.v$.requestDetails,
        [
          ['required', this.$t('projects.dialogs.ProjectsNewDataroomDialogContactForm.requestDetailsRequired')],
        ],
      )
    },
    termsCheckedError () {
      return formFieldErrors(
        this.v$.isPrivacyPolicyCheck,
        [
          ['sameAs', this.$t('projects.dialogs.ProjectsNewDataroomDialogContactForm.termsCheckedValidationError')],
        ],
      )
    },
  },
  created () {
    this.telephone = this.profile.telephone
  },
  beforeUnmount () {
    if (this.quoteRequestFormIntervalTimerId !== -1) {
      clearInterval(this.quoteRequestFormIntervalTimerId)
    }
  },
  methods: {
    isValidPhoneNumber (phoneNumber) {
      if (!phoneNumber) {
        return true // We let the "required" validator handle empty phone numbers
      }

      const parsingResult = parsePhoneNumber(phoneNumber)
      return parsingResult.valid
    },
    async sendRequest () {
      this.v$.$touch()
      const isFormValid = !this.v$.$invalid
      if (!isFormValid) {
        return false
      }

      try {
        this.$refs.quoteRequestForm.submit()
        this.quoteRequestPending = true
        this.quoteRequestFormIntervalTimerId = setInterval(() => {
          // If the iframe is trying to reload the page, the form has been submitted
          if (this.$refs.formIframe.contentWindow.location.hostname === globalThis.location.hostname) {
            clearInterval(this.quoteRequestFormIntervalTimerId)
            this.onFormSubmitSuccess()
          }
        }, FORM_SUCCESS_POLLING_INTERVAL)

        // Just in case, we set an onload handler to the iframe
        // When it "loads", it means the form was submit and has finished redirecting,
        // So it is done. This unfortunately means we may wait until
        // The newly booted app from the redirection is fully loaded
        this.$refs.formIframe.onload = this.onFormSubmitSuccess

      } catch (error) {
        console.error(error)
        toast.error(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestErrorMessage'))
        return false
      }

      return true
    },
    onFormSubmitSuccess () {
      this.$emit('formSubmitSuccess')
    },
    handlePhoneChange (input, event) {
      this.telephone = event.number || input
    },
  },
}
</script>

<style scoped lang="scss">
fieldset {
  border: none;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgb(var(--v-theme-grey-900));
}

.privacy-policy-check {
  padding-top: 4px;

  :deep(.v-selection-control) {
    align-items: flex-start;
  }
}
</style>
